import { useEffect, useState } from 'react'
import type { PlayerLabels, Station, Track } from './samApi'

type PlayState = 'idle' | 'loading' | 'playing'

type Props = {
  labels: PlayerLabels
  track: Track | null
  station: Station | null
  coverSrc: string
  coverLoaded: boolean
  playState: PlayState
  muted: boolean
  silent: boolean
  volume: number
  volumeSupported: boolean
  onToggle: () => void
  onToggleMute: () => void
  onVolume: (value: number) => void
  onClose: () => void
}

// Compact player pinned to the bottom of the viewport (radio page only). It is a
// pure control surface for the single <audio> owned by RadioPlayer — all state
// and handlers are passed in, so there is exactly one stream playing.
function FloatingBar({
  labels,
  track,
  station,
  coverSrc,
  coverLoaded,
  playState,
  muted,
  silent,
  volume,
  volumeSupported,
  onToggle,
  onToggleMute,
  onVolume,
  onClose
}: Props) {
  // Reserve space so the fixed bar never covers the footer.
  useEffect(() => {
    document.body.classList.add('has-radio-floating')
    return () => document.body.classList.remove('has-radio-floating')
  }, [])

  // Play the slide-out, then actually unmount (parent clears the portal). Skip
  // the animation entirely when the user prefers reduced motion.
  const [closing, setClosing] = useState(false)
  const handleClose = (): void => {
    if (closing) return
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduce) {
      onClose()
      return
    }
    setClosing(true)
    window.setTimeout(onClose, 400)
  }

  // Reuse .radio-player + its state classes so the toggle / cover / mute icon
  // states (play↔pause, spinner, muted) work without duplicating those rules.
  const classes = [
    'radio-player',
    'radio-player--floating',
    playState === 'loading' && 'is-loading',
    playState === 'playing' && 'is-playing',
    silent && 'is-muted',
    coverLoaded && 'has-cover'
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={`radio-player-floating${closing ? ' is-closing' : ''}`}>
      <div className={classes}>
        <button
          type="button"
          className="radio-player__toggle"
          aria-label={playState === 'playing' ? labels.pause : labels.play}
          onClick={onToggle}
        >
          <span className="radio-player__spinner" aria-hidden="true" />
        </button>

        <div className="radio-player__cover" aria-hidden="true">
          {coverSrc && (
            <img className="radio-player__cover-img" alt="" src={coverSrc} />
          )}
          <span className="radio-player__cover-fallback" aria-hidden="true" />
        </div>

        <div className="radio-player__meta">
          <p className="radio-player__station">
            <span className="radio-player__live">
              <span className="radio-player__live-dot" />
              {labels.live}
            </span>
            <span className="radio-player__station-name">
              {station?.name ?? labels.stationName}
            </span>
          </p>
          <p className="radio-player__title">{track?.title ?? ''}</p>
          <p className="radio-player__artist">{track?.artist ?? ''}</p>
        </div>

        <div className="radio-player__volume">
          <button
            type="button"
            className="radio-player__mute"
            aria-label={muted ? labels.unmute : labels.mute}
            onClick={onToggleMute}
          />
          {volumeSupported && (
            <input
              type="range"
              className="radio-player__volume-slider"
              min={0}
              max={100}
              value={volume}
              aria-label={labels.volume}
              onChange={(event) => onVolume(Number(event.target.value))}
            />
          )}
        </div>
      </div>

      <button
        type="button"
        className="radio-player-floating__close"
        aria-label={labels.close}
        onClick={handleClose}
      />
    </div>
  )
}

export default FloatingBar
